/**
 * wayai push [hubs/<hub>|bases/<base>] [--hub <id|name>|--base <id|name>] [-y]
 *   — parse local files, diff remote, sync to preview
 *
 * ONE verb, routing by workspace subtree: `hubs/` pushes hub config-as-code
 * (below), `bases/` pushes base config-as-code
 * (`data/config-as-code/sync.ts`). An invocation that names a target in both
 * subtrees is REFUSED before any network call and before any file write, never
 * merged and never silently narrowed to one side — the routing precedence and
 * the three refusals live in `lib/subtree-routing.ts`. Each subtree keeps its own
 * preview-only rule: production is `wayai publish` for a hub and
 * `wayai bases promote` for a base.
 *
 * Within the hubs subtree the hub is derived from the workspace, as before:
 *   - cwd is inside a hub folder → use that hub
 *   - exactly one hub in the hubs dir → use it
 *   - multiple hubs → require `--hub <uuid|folder-name>` to disambiguate
 *
 * If a hub folder has hub.yaml without a `hub_id`, push creates the hub on the
 * platform first, then pushes its config.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { parseHubFolder } from '../lib/parser.js';
import { materializeHubFolder } from '../lib/hub-materializer.js';
import { assertResourcePayloadFitsWithoutPruning, prepareResourcePayload } from '../lib/resource-files.js';
import { printDiff } from '../lib/diff-display.js';
import { confirm, slugify, resolveHubYamlPath, UUID_RE } from '../lib/utils.js';
import {
  autoRenameHubFolder,
  filterPreviewHubs,
  resolveWorkspaceDir,
  scanWorkspaceHubs,
  scanNewHubs,
  findGitRoot,
  findEnclosingHubFolder,
  resolvePushTarget,
  type HubLocation,
  type NewHubLocation,
} from '../lib/workspace.js';
import { warnLayoutOnce, hubsDirLabel } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertInScope, assertScopeAllowsCreation, seedScopeIfEmpty } from '../lib/worktree-scope.js';
import { requireSubtree } from '../lib/subtree-routing.js';
import type { CiSyncResponse, HubAsCodePayload } from '../lib/types.js';
import { readRealFileOrThrow, requireRealHubFolder, writeFileNoFollow } from '../lib/fs-safety.js';

interface ParsedArgs {
  autoConfirm: boolean;
  label?: string;
}

/**
 * Everything that is NOT the target. The target — `--hub`, a positional, the
 * repo default — is resolved by the subtree router, which has to read it anyway
 * to decide which subtree this invocation belongs to.
 */
function parseArgs(args: string[]): ParsedArgs {
  let autoConfirm = false;
  let label: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--yes' || arg === '-y') {
      autoConfirm = true;
    } else if (arg === '--label' && args[i + 1]) {
      label = args[++i];
    }
  }

  return { autoConfirm, label };
}

/**
 * Whether to warn that a hand-edited `hub.yaml` `preview_label` will be ignored by
 * push. True only when the local file carries a present, truthy label that differs
 * from the server's. Guards:
 *   - an absent/empty local label never warns (it's the normal echo-stripped state);
 *   - `serverLabel === undefined` (older backend that doesn't return it) never warns.
 */
export function shouldWarnIgnoredPreviewLabel(
  localLabel: string | null | undefined,
  serverLabel: string | null | undefined,
): boolean {
  return Boolean(localLabel) && serverLabel !== undefined && localLabel !== serverLabel;
}

export function printSyncResult(result: CiSyncResponse): void {
  const applyErrors = result.apply_errors ?? result.errors;
  if (applyErrors && applyErrors.length > 0) {
    printErrors(applyErrors);
    // The backend reports `internal` entries to Sentry with their original
    // exception and entity context. Keep the CLI's summary expected so it does
    // not create a second, content-free issue after rendering the details.
    throw expected(`Sync failed with ${applyErrors.length} error(s). No completion was reported; inspect the errors above.`);
  }

  console.log(`\nSync complete. Preview hub: ${result.preview_hub_id}`);
  const c = result.changes;
  const parts: string[] = [];
  if (c.hub_updated) parts.push('hub updated');
  if (c.connections_created > 0) parts.push(`${c.connections_created} connection(s) created`);
  if (c.connections_updated > 0) parts.push(`${c.connections_updated} connection(s) updated`);
  if (c.connections_deleted > 0) parts.push(`${c.connections_deleted} connection(s) deleted`);
  if (c.agents_created > 0) parts.push(`${c.agents_created} agent(s) created`);
  if (c.agents_updated > 0) parts.push(`${c.agents_updated} agent(s) updated`);
  if (c.agents_deleted > 0) parts.push(`${c.agents_deleted} agent(s) deleted`);
  if (c.tools_created > 0) parts.push(`${c.tools_created} tool(s) created`);
  if (c.tools_updated > 0) parts.push(`${c.tools_updated} tool(s) updated`);
  if (c.tools_deleted > 0) parts.push(`${c.tools_deleted} tool(s) deleted`);
  if (c.states_created > 0) parts.push(`${c.states_created} state(s) created`);
  if (c.states_updated > 0) parts.push(`${c.states_updated} state(s) updated`);
  if (c.states_deleted > 0) parts.push(`${c.states_deleted} state(s) deleted`);
  if (c.evals_created > 0) parts.push(`${c.evals_created} eval(s) created`);
  if (c.evals_updated > 0) parts.push(`${c.evals_updated} eval(s) updated`);
  if (c.evals_deleted > 0) parts.push(`${c.evals_deleted} eval(s) deleted`);
  if (c.journeys_created > 0) parts.push(`${c.journeys_created} journey(s) created`);
  if (c.journeys_updated > 0) parts.push(`${c.journeys_updated} journey(s) updated`);
  if (c.journeys_deleted > 0) parts.push(`${c.journeys_deleted} journey(s) deleted`);
  if (c.teams_created > 0) parts.push(`${c.teams_created} team(s) created`);
  if (c.teams_updated > 0) parts.push(`${c.teams_updated} team(s) updated`);
  if (c.teams_deleted > 0) parts.push(`${c.teams_deleted} team(s) deleted`);
  if (c.resources_created > 0) parts.push(`${c.resources_created} resource(s) created`);
  if (c.resources_updated > 0) parts.push(`${c.resources_updated} resource(s) updated`);
  if (c.resources_deleted > 0) parts.push(`${c.resources_deleted} resource(s) deleted`);
  if (c.resource_files_uploaded > 0) parts.push(`${c.resource_files_uploaded} resource file(s) uploaded`);
  if (c.resource_files_deleted > 0) parts.push(`${c.resource_files_deleted} resource file(s) deleted`);
  if (c.resource_links_created > 0) parts.push(`${c.resource_links_created} resource link(s) created`);
  if (c.resource_links_updated > 0) parts.push(`${c.resource_links_updated} resource link(s) updated`);
  if (c.resource_links_deleted > 0) parts.push(`${c.resource_links_deleted} resource link(s) deleted`);
  if (c.outbound_contacts_created > 0) parts.push(`${c.outbound_contacts_created} outbound contact(s) created`);
  if (c.outbound_contacts_updated > 0) parts.push(`${c.outbound_contacts_updated} outbound contact(s) updated`);
  if (c.outbound_contacts_deleted > 0) parts.push(`${c.outbound_contacts_deleted} outbound contact(s) deleted`);
  if (c.outbound_lists_created > 0) parts.push(`${c.outbound_lists_created} outbound list(s) created`);
  if (c.outbound_lists_updated > 0) parts.push(`${c.outbound_lists_updated} outbound list(s) updated`);
  if (c.outbound_lists_deleted > 0) parts.push(`${c.outbound_lists_deleted} outbound list(s) deleted`);
  if (c.automations_created > 0) parts.push(`${c.automations_created} automation(s) created`);
  if (c.automations_updated > 0) parts.push(`${c.automations_updated} automation(s) updated`);
  if (c.automations_deleted > 0) parts.push(`${c.automations_deleted} automation(s) deleted`);
  if (parts.length > 0) {
    console.log(`Changes: ${parts.join(', ')}`);
  }

  if (result.warnings && result.warnings.length > 0) {
    printWarnings(result.warnings.map((w) => w.message));
  }
}

/** Render a yellow-highlighted "Warnings (N)" block. Shared by push + sync output. */
export function printWarnings(warnings: string[]): void {
  if (warnings.length === 0) return;
  console.log(`\n\x1b[33mWarnings (${warnings.length}):\x1b[0m`);
  for (const w of warnings) {
    console.log(`  \x1b[33m! ${w}\x1b[0m`);
  }
}

/** Render a red-highlighted "Errors (N)" block for fatal apply failures. */
export function printErrors(
  errors: Array<NonNullable<CiSyncResponse['apply_errors']>[number] | string>,
): void {
  if (errors.length === 0) return;
  console.error(`\n\x1b[31mErrors (${errors.length}):\x1b[0m`);
  for (const error of errors) {
    const message = typeof error === 'string' ? error : error.message;
    console.error(`  \x1b[31m! ${message}\x1b[0m`);
  }
}

/** Cap the change list so a one-time normalization delta doesn't flood the terminal. */
const LOCAL_CHANGE_LIST_CAP = 20;

/**
 * Announce the working-tree delta a materialize produced so silently-written files aren't
 * committed unread (gh #3410): `- <path>` for removed files, `~ <path>` for written/updated
 * ones. Prints "Local files already in sync." when nothing changed. Shared by push's
 * post-push sync, pull's apply/overwrite/metadata-sync branches, and replicate.
 *
 * Removals print FIRST and each list is capped INDEPENDENTLY: a deletion is the higher-signal
 * half (a file leaving the working tree), so a large `changed` list (e.g. a one-time
 * normalization) must never push every `removed` line behind a single "…and N more".
 */
export function printLocalFileChanges(delta: { changed: string[]; removed: string[] }): void {
  if (delta.changed.length === 0 && delta.removed.length === 0) {
    console.log('Local files already in sync.');
    return;
  }
  console.log('Local files updated from server:');
  const emit = (paths: string[], sigil: string) => {
    const sorted = [...paths].sort();
    for (const p of sorted.slice(0, LOCAL_CHANGE_LIST_CAP)) console.log(`  ${sigil} ${p}`);
    if (sorted.length > LOCAL_CHANGE_LIST_CAP) {
      console.log(`  …and ${sorted.length - LOCAL_CHANGE_LIST_CAP} more ${sigil === '-' ? 'removed' : 'changed'}`);
    }
  };
  emit(delta.removed, '-');
  emit(delta.changed, '~');
}

/**
 * Auto-rename agent .yaml and .md files when agents have been renamed (matched by ID).
 * Compares local agent names against remote agent names via pull,
 * then renames old-slug.yaml → new-slug.yaml and old-slug.md → new-slug.md.
 * Handles slug swaps via temp files.
 * Also updates explicit `instructions` paths in the hub YAML to match.
 */
export async function autoRenameAgentFiles(
  client: ApiClient,
  hubId: string,
  hubFolder: string,
  organizationId?: string,
  remoteConfig?: HubAsCodePayload,
): Promise<void> {
  // Renames and deletes below act inside agents/ and rewrite hub.yaml: refuse a
  // symlinked hub folder or subdir before touching anything.
  requireRealHubFolder(hubFolder, false);

  // Collect agents with IDs from per-agent YAML files or main YAML
  const agentsDir = path.join(hubFolder, 'agents');
  let agentsWithIds: Array<{ id: string; name: string }> = [];

  // Try per-agent YAML files first
  if (fs.existsSync(agentsDir)) {
    const yamlFiles = fs.readdirSync(agentsDir).filter((f) => f.endsWith('.yaml'));
    for (const file of yamlFiles) {
      // Read no-follow, before any rename: a rename decided from a link target's contents
      // is the damage, so a symlink refusal stops the push here rather than being skipped.
      const content = readRealFileOrThrow(hubFolder, path.join(agentsDir, file)) ?? '';
      try {
        const agent = yaml.load(content) as { id?: string; name?: string } | undefined;
        if (agent?.id && agent.name) {
          agentsWithIds.push({ id: agent.id, name: agent.name });
        }
      } catch {
        console.warn(`  Warning: could not parse agents/${file}, skipping`);
      }
    }
  }

  // Fall back to inline agents from main YAML
  if (agentsWithIds.length === 0) {
    const yamlPath = resolveHubYamlPath(hubFolder);
    if (!yamlPath) return;

    const yamlContent = readRealFileOrThrow(hubFolder, yamlPath) ?? '';
    const config = yaml.load(yamlContent) as { agents?: Array<{ id?: string; name?: string }> };
    agentsWithIds = (config.agents || []).filter((a): a is { id: string; name: string } => !!a.id && !!a.name);
  }

  if (agentsWithIds.length === 0) return;

  // Fetch remote state to detect renames (extra round-trip, only when agents have IDs)
  console.log('Checking for agent renames...');
  let remote = remoteConfig;
  if (!remote) {
    try {
      remote = await client.pull(hubId, organizationId);
    } catch (err) {
      console.warn(`  Warning: could not fetch remote state for auto-rename (${err instanceof Error ? err.message : String(err)}). File renames skipped.`);
      return;
    }
  }

  // Build remote agent id → name map
  const remoteAgentNames = new Map<string, string>();
  for (const agent of remote.agents || []) {
    if (agent.id) {
      remoteAgentNames.set(agent.id, agent.name);
    }
  }

  // Collect renames needed (old slug → new slug)
  const renames: Array<{ oldSlug: string; newSlug: string }> = [];
  for (const agent of agentsWithIds) {
    const remoteName = remoteAgentNames.get(agent.id!);
    if (!remoteName || remoteName === agent.name) continue;

    const oldSlug = slugify(remoteName);
    const newSlug = slugify(agent.name!);
    if (oldSlug !== newSlug) {
      renames.push({ oldSlug, newSlug });
    }
  }

  if (renames.length === 0) return;

  if (!fs.existsSync(agentsDir)) return;

  // Clean up orphaned temp files from a previous interrupted rename
  for (const file of fs.readdirSync(agentsDir)) {
    if (file.startsWith('__rename_temp_') && (file.endsWith('.md') || file.endsWith('.yaml'))) {
      console.warn(`  Warning: removing orphaned temp file agents/${file}`);
      fs.unlinkSync(path.join(agentsDir, file));
    }
  }

  // Helper to rename a file if it exists (for both .yaml and .md extensions)
  const renameFileIfExists = (dir: string, oldName: string, newName: string): boolean => {
    const oldPath = path.join(dir, oldName);
    const newPath = path.join(dir, newName);
    if (!fs.existsSync(oldPath)) return false;
    fs.renameSync(oldPath, newPath);
    return true;
  };

  // Detect slug swaps: if newSlug matches another rename's oldSlug
  const oldSlugs = new Set(renames.map((r) => r.oldSlug));
  const newSlugs = new Set(renames.map((r) => r.newSlug));
  const hasConflicts = renames.some((r) => oldSlugs.has(r.newSlug)) || newSlugs.size !== renames.length;

  // Track which renames actually completed (file existed and was moved)
  const completedRenames: Array<{ oldSlug: string; newSlug: string }> = [];
  const extensions = ['.yaml', '.md'];

  if (hasConflicts) {
    // Rename through temp files to avoid collisions
    const tempRenames: Array<{ oldSlug: string; tempName: string; newSlug: string }> = [];
    for (const { oldSlug, newSlug } of renames) {
      const tempName = `__rename_temp_${oldSlug}_${Date.now()}`;
      tempRenames.push({ oldSlug, tempName, newSlug });
    }

    // Step 1: old → temp (both extensions)
    for (const { oldSlug, tempName } of tempRenames) {
      for (const ext of extensions) {
        renameFileIfExists(agentsDir, `${oldSlug}${ext}`, `${tempName}${ext}`);
      }
    }

    // Step 2: temp → new (both extensions)
    for (const { oldSlug, tempName, newSlug } of tempRenames) {
      let anyRenamed = false;
      for (const ext of extensions) {
        if (renameFileIfExists(agentsDir, `${tempName}${ext}`, `${newSlug}${ext}`)) {
          anyRenamed = true;
        }
      }
      if (anyRenamed) {
        completedRenames.push({ oldSlug, newSlug });
        console.log(`  Renamed agents/${oldSlug}.* → agents/${newSlug}.*`);
      }
    }
  } else {
    // No conflicts — direct renames
    for (const { oldSlug, newSlug } of renames) {
      // Check if any file with old slug exists
      const hasOldFile = extensions.some((ext) => fs.existsSync(path.join(agentsDir, `${oldSlug}${ext}`)));
      if (!hasOldFile) continue;

      // Check for target conflicts
      const hasNewFile = extensions.some((ext) => fs.existsSync(path.join(agentsDir, `${newSlug}${ext}`)));
      if (hasNewFile) {
        console.warn(`  Warning: skipping rename agents/${oldSlug}.* → agents/${newSlug}.* (target already exists)`);
        continue;
      }

      for (const ext of extensions) {
        renameFileIfExists(agentsDir, `${oldSlug}${ext}`, `${newSlug}${ext}`);
      }
      completedRenames.push({ oldSlug, newSlug });
      console.log(`  Renamed agents/${oldSlug}.* → agents/${newSlug}.*`);
    }
  }

  if (completedRenames.length === 0) return;

  // Update explicit instructions paths in hub YAML for completed renames only.
  const mainYamlPath = resolveHubYamlPath(hubFolder);
  if (mainYamlPath) {
    const mainYamlContent = readRealFileOrThrow(hubFolder, mainYamlPath) ?? '';
    const substitutionMap = new Map<string, string>();
    for (const { oldSlug, newSlug } of completedRenames) {
      substitutionMap.set(`agents/${oldSlug}.md`, `agents/${newSlug}.md`);
    }
    const escapedKeys = Array.from(substitutionMap.keys())
      .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const updatedYaml = mainYamlContent.replace(
      new RegExp(`(instructions:\\s+)(${escapedKeys.join('|')})`, 'g'),
      (_match, prefix: string, pathMatch: string) => `${prefix}${substitutionMap.get(pathMatch) ?? pathMatch}`,
    );
    if (updatedYaml !== mainYamlContent) {
      writeFileNoFollow(hubFolder, mainYamlPath, Buffer.from(updatedYaml, 'utf-8'));
      console.log(`  Updated instructions paths in ${path.basename(mainYamlPath)}`);
    }
  }
}

/** Pull remote config after push, sync local files, and rename hub folder if needed. Exported for tests. */
export async function syncAfterPush(
  client: ApiClient,
  hubId: string,
  hubFolder: string,
  organizationId?: string
): Promise<void> {
  try {
    const remoteConfig = await client.pull(hubId, organizationId);
    const delta = await materializeHubFolder(hubFolder, remoteConfig);
    printLocalFileChanges(delta);

    const finalFolder = autoRenameHubFolder(hubFolder, remoteConfig.hub.name, remoteConfig.hub_environment, hubId, remoteConfig.preview_label, remoteConfig.branch_name);
    if (finalFolder !== hubFolder) {
      console.log(`Hub folder is now ${finalFolder}`);
    }
  } catch (err) {
    console.warn(`Warning: could not sync local files after push (${err instanceof Error ? err.message : String(err)})`);
  }
}

async function pushSingleHub(
  client: ApiClient,
  hubId: string,
  hubFolder: string,
  autoConfirm: boolean,
  organizationId?: string,
  opts?: { skipAgentRename?: boolean }
): Promise<boolean> {
  let remoteConfig: HubAsCodePayload | undefined;
  try {
    remoteConfig = await client.pull(hubId, organizationId);
  } catch (err) {
    const fallbackConfig = parseHubFolder(hubFolder);
    assertResourcePayloadFitsWithoutPruning(fallbackConfig, err);
  }

  if (!opts?.skipAgentRename) {
    // Auto-rename agent .md files if agents were renamed (before parsing)
    await autoRenameAgentFiles(client, hubId, hubFolder, organizationId, remoteConfig);
  }

  console.log('Parsing local configuration...');
  const localConfig = parseHubFolder(hubFolder);
  const wireConfig = remoteConfig ? prepareResourcePayload(localConfig, remoteConfig) : localConfig;

  console.log('Computing diff...');
  const diffResult = await client.diff(hubId, wireConfig, 'push', organizationId);

  // Render diff-time warnings (e.g. native/delegation tools dropped due to
  // missing connections) before any short-circuit. Without this, a push where
  // every change was silently dropped would just say "No changes to push". See
  // gh #1538.
  printWarnings(diffResult.warnings);

  // preview_label is server-owned (set via `wayai relabel` / replicate) and the
  // push diff engine ignores it — so a hand-edited hub.yaml label would be
  // silently dropped. Warn when the local file carries a truthy label that differs
  // from the server's.
  if (shouldWarnIgnoredPreviewLabel(localConfig.preview_label, diffResult.preview_label)) {
    console.warn(
      `Note: preview_label "${localConfig.preview_label}" in hub.yaml is server-owned and ignored on push ` +
      `(server value: ${diffResult.preview_label ?? 'none'}). Use \`wayai relabel\` to change it.`,
    );
  }

  if (!diffResult.has_changes) {
    // Still rename folder if hub name changed (content matches but folder slug is stale)
    const finalFolder = autoRenameHubFolder(hubFolder, localConfig.hub.name, localConfig.hub_environment, hubId, localConfig.preview_label, localConfig.branch_name);
    if (finalFolder === hubFolder) {
      console.log('No changes to push.');
    } else {
      console.log('No changes to push; folder renamed.');
    }
    seedScopeIfEmpty('hubs', hubId);
    return true;
  }

  console.log('\nChanges to push:');
  printDiff(diffResult.summary);

  if (!autoConfirm) {
    const ok = await confirm('Push these changes?');
    if (!ok) {
      console.log('Cancelled.');
      return false;
    }
  }

  console.log('Pushing changes...');
  const result = await client.push(hubId, wireConfig, organizationId);
  printSyncResult(result);

  await syncAfterPush(client, hubId, hubFolder, organizationId);

  seedScopeIfEmpty('hubs', hubId);
  return true;
}

/**
 * Resolve a single existing hub from the workspace: `--hub` selector (matched by
 * id or folder name, exits if not found) | the enclosing folder | the sole
 * preview. Returns null when 0 or >1 previews match without a selector, so the
 * caller decides what to do (push falls through to new-hub creation; relabel errors).
 * Shared with `wayai relabel` (which needs the resolved `HubLocation.hubEnvironment`).
 */
export function selectExistingHub(
  workspaceDir: string,
  allHubs: HubLocation[],
  selector: string | undefined,
  wsLabel: string,
): HubLocation | null {
  if (selector) {
    const match = allHubs.find((h) =>
      h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (!match) {
      console.error(`No hub matching --hub ${selector} found in ${wsLabel}/.`);
      process.exit(1);
    }
    return match;
  }

  const enclosing = findEnclosingHubFolder(workspaceDir);
  if (enclosing) return enclosing;

  // Auto-select ignores read-only production mirror folders (push can't target
  // production anyway), so a preview + its mirror still resolves to the preview.
  const previewHubs = filterPreviewHubs(allHubs);
  if (previewHubs.length === 1) return previewHubs[0];
  return null;
}

/**
 * Create a brand-new hub from an idless folder, then push its initial config.
 * Shared by `wayai push` (when resolution lands on a new folder) and `wayai
 * create` (the explicit verb — gh #3117). Order: confirm → createHub → write back
 * `hub_id`/`hub_environment` → seed the scope → push. Callers MUST run
 * `assertScopeAllowsCreation` first; `seedScopeIfEmpty` only seeds a worktree
 * whose hubs axis is empty.
 */
export async function createAndPushNewHub(
  client: ApiClient,
  newHub: NewHubLocation,
  opts: { autoConfirm: boolean; label?: string; organizationId: string },
): Promise<void> {
  const hubType = newHub.hubType || 'chat';
  console.log(`\nNew hub: "${newHub.hubName}" (${hubType})`);

  if (!opts.autoConfirm) {
    const ok = await confirm('Create this hub on the platform?');
    if (!ok) {
      console.log('Cancelled.');
      return;
    }
  }

  // Checked before the hub is created on the platform, so a refusal leaves nothing behind.
  requireRealHubFolder(newHub.hubFolder, false);
  const hubYamlPath = resolveHubYamlPath(newHub.hubFolder);
  if (!hubYamlPath) {
    console.error(`hub.yaml not found in ${newHub.hubFolder}. Cannot proceed with hub creation.`);
    process.exit(1);
  }
  const content = readRealFileOrThrow(newHub.hubFolder, hubYamlPath) ?? '';

  console.log('Creating hub...');
  const createResult = await client.createHub(opts.organizationId, {
    hubName: newHub.hubName,
    hubType,
    previewLabel: opts.label?.trim() || undefined,
  });
  const createdHub = createResult.data[0];
  const hubId = createdHub.hub_id;

  if (!UUID_RE.test(hubId)) {
    console.error(`Invalid hub_id received from server: ${hubId}`);
    process.exit(1);
  }

  console.log(`Hub created: ${createdHub.hub_name} (${hubId})`);

  // Update hub.yaml with hub_id and hub_environment
  const hasVersion = content.match(/^version:\s/m);
  let updated: string;
  if (hasVersion) {
    updated = content.replace(
      /^(version:\s.*\n)/m,
      `$1hub_id: "${hubId}"\nhub_environment: preview\n`
    );
  } else {
    updated = `version: 1\nhub_id: "${hubId}"\nhub_environment: preview\n${content}`;
  }
  writeFileNoFollow(newHub.hubFolder, hubYamlPath, Buffer.from(updated, 'utf-8'));

  // Seed the scope only after hub.yaml is persisted, so a write failure can't
  // leave the worktree scoped to a hub with no local files.
  seedScopeIfEmpty('hubs', hubId);

  // Push the new hub (skip autoRenameAgentFiles — no remote agents exist)
  await pushSingleHub(client, hubId, newHub.hubFolder, opts.autoConfirm, opts.organizationId, { skipAgentRename: true });
}

/**
 * Print the error + candidate list for an unresolved `wayai push` target, then
 * exit. `selector_miss` and `ambiguous` list EVERY candidate (created + pending)
 * so the new folder that id-bearing resolution used to hide is visible (gh #3117).
 * Exported for unit testing (mirrors the other exported helpers in this file).
 */
export function reportUnresolvedPushTarget(
  reason: 'selector_miss' | 'none' | 'ambiguous',
  existingHubs: HubLocation[],
  newHubs: NewHubLocation[],
  selector: string | undefined,
  wsLabel: string,
): never {
  if (reason === 'none') {
    // `resolvePushTarget` returns `none` when nothing is pushable: no preview and
    // no new folder. `existingHubs` may still hold read-only production mirrors
    // (filtered out of the auto-select count), so name them rather than claiming
    // the workspace is empty.
    if (existingHubs.length > 0) {
      console.error(`No pushable hub found in ${wsLabel}/ — only read-only production mirror folder(s). Edit the linked preview hub, pull one, or create a new hub with \`wayai create\`:`);
      for (const h of existingHubs) {
        console.error(`  ${path.basename(h.hubFolder)} (${h.hubId}, production mirror)`);
      }
    } else {
      console.error(`No hub folders found. Create hub files in ${wsLabel}/<hub>/hub.yaml with \`hub: { name: ... }\` and run \`wayai push\` (or \`wayai create\`).`);
    }
    process.exit(1);
  }

  if (reason === 'selector_miss') {
    // Names the selector, not the flag — it can now arrive as a positional
    // (`wayai push hubs/<hub>`) or as the repo default.
    console.error(`No hub matching "${selector}" found in ${wsLabel}/. Available:`);
  } else {
    console.error(`Multiple hub folders found in ${wsLabel}/. Pass --hub <uuid|folder-name> to choose, or run from inside a hub folder:`);
  }
  for (const h of existingHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
  }
  for (const h of newHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (new — "${h.hubName}")`);
  }
  process.exit(1);
}

export async function pushCommand(args: string[]): Promise<void> {
  // Subtree routing FIRST — ahead of the repo config, the login, the first
  // request and the workspace-file scaffolding below, so a mixed invocation is
  // refused before it can touch the network or the working tree.
  // Resolved once: `findGitRoot` spawns `git`, and both the router and the
  // command below need the answer.
  const gitRoot = findGitRoot();
  const route = requireSubtree('push', args, gitRoot);
  if (gitRoot) warnLayoutOnce(gitRoot);
  if (route.subtree === 'bases') {
    const { pushBase } = await import('../data/config-as-code/sync.js');
    await pushBase(gitRoot, route.selector, args);
    return;
  }

  const { autoConfirm, label } = parseArgs(args);
  // The router resolved the hub selector from `--hub` or the positional; the
  // within-subtree fallbacks (enclosing folder, sole candidate) stay below.
  const hubSelector = route.selector;
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const workspaceDir = resolveWorkspaceDir();
  const wsLabel = hubsDirLabel(gitRoot);
  if (!fs.existsSync(workspaceDir)) {
    console.error(`No ${wsLabel}/ directory found. Run \`wayai pull\` first or create hub files in ${wsLabel}/<hub>/hub.yaml.`);
    process.exit(1);
  }

  // Scan once and reuse — the resolver weighs created (id-bearing) and new
  // (idless) folders as CO-EQUAL candidates (gh #3117): the old resolver
  // auto-selected the sole existing preview and silently ignored new folders.
  const existingHubs = scanWorkspaceHubs(workspaceDir);
  const newHubs = scanNewHubs(workspaceDir);

  const resolution = resolvePushTarget(workspaceDir, existingHubs, newHubs, hubSelector);
  if (!resolution.ok) {
    reportUnresolvedPushTarget(resolution.reason, existingHubs, newHubs, hubSelector, wsLabel);
  }

  if (resolution.target.kind === 'existing') {
    const existing = resolution.target.hub;
    // Refuse a read-only production mirror early (reached via cwd or explicit
    // --hub). Defense-in-depth: the route + DO already block production writes,
    // but failing here avoids a backend round-trip and a noisy CI failure.
    if (existing.hubEnvironment === 'production') {
      console.log('This is a read-only production mirror — production hubs cannot be pushed. Edit the linked preview hub instead.');
      return;
    }
    // --label only applies when CREATING a hub; on an existing hub the label is
    // server-owned and changed via `wayai relabel`. Warn rather than silently drop.
    if (label) {
      console.warn('Note: --label is only used when creating a new hub. To change an existing hub\'s label, use `wayai relabel`.');
    }
    // Name the resolved target before diffing/pushing so `wayai push -y` can't
    // silently act on a hub the user didn't mean (gh #3117).
    console.log(`Target hub: ${path.basename(existing.hubFolder)} (${existing.hubId})`);
    assertInScope('hubs', existing.hubId, path.basename(existing.hubFolder));
    await pushSingleHub(client, existing.hubId, existing.hubFolder, autoConfirm, organizationId);
    return;
  }

  // New (idless) folder → create it, then push. `assertScopeAllowsCreation`
  // refuses to create a hub inside a worktree already scoped to other hubs.
  assertScopeAllowsCreation('hubs', resolution.target.hub.hubName);
  await createAndPushNewHub(client, resolution.target.hub, { autoConfirm, label, organizationId });
}
